import i18n from '@buy-viasat/utils/src/lib/config/i18n';
import { createSelector } from 'reselect';
import { selectAddressDomain } from '../address';
import { RootState } from '../types';
import { initialServiceabilityState } from './';
import { ServiceabilityState } from './types';

const VERMONT = 'VT';

export const selectApp = (state: RootState) => state.app;

export const selectServiceabilityDomain = (state: RootState): ServiceabilityState => {
  return state.serviceability || initialServiceabilityState;
};

export const selectHasFiberMduAddress = createSelector(
  [selectServiceabilityDomain],
  (serviceabilityState: ServiceabilityState) => serviceabilityState.hasFiberMduAddress,
);

export const selectUnsanitizedAddressLines = createSelector(
  [selectServiceabilityDomain],
  (serviceabilityState: ServiceabilityState) => serviceabilityState.unsanitizedAddressLines,
);

export const selectFiberMultipleAddressesForPreviewProducts = createSelector(
  [selectServiceabilityDomain],
  (serviceabilityState: ServiceabilityState) => serviceabilityState.fiberMultipleAddressesForPreviewProducts,
);

export const selectShowMap = createSelector(
  [selectServiceabilityDomain],
  (serviceabilityState: ServiceabilityState) => serviceabilityState.showMap,
);

export const selectIsServiceabilityLoading = createSelector(
  [selectServiceabilityDomain],
  (serviceabilityState: ServiceabilityState) => serviceabilityState.isServiceabilityLoading,
);
export const selectCoordinates = createSelector(
  [selectServiceabilityDomain],
  ({ longitude, latitude }: ServiceabilityState) => ({ longitude, latitude }),
);

export const selectHouseNumber = createSelector(
  [selectServiceabilityDomain],
  ({ serviceAddress: { houseNumber } }) => ({
    houseNumber: houseNumber.value,
  }),
);
export const selectServiceAddressValues = createSelector(
  [selectServiceabilityDomain, selectApp],
  ({ serviceAddress }) => {
    const { addressLines, municipality, region, postalCode, countryCode } = serviceAddress;

    return {
      addressLines: addressLines.value,
      municipality: municipality.value,
      region: region.value,
      postalCode: postalCode.value,
      countryCode: countryCode.value,
    };
  },
);
export const selectServiceAddressPostalCode = createSelector(
  [selectServiceabilityDomain],
  ({ serviceAddress: { postalCode } }) => postalCode,
);
export const selectServiceAddressRegion = createSelector(
  [selectServiceabilityDomain],
  ({ serviceAddress: { region } }) => region,
);
export const selectScrubLocation = createSelector(
  [selectServiceabilityDomain],
  (serviceabilityState: ServiceabilityState) => serviceabilityState.scrubbedLocation,
);
export const selectInlineScrubAddress = createSelector(
  [selectServiceabilityDomain, selectApp],
  ({
    scrubbedLocation: {
      address: { addressLines, municipality, region, postalCode, houseNumber },
    },
  }) => {
    const [street, aptNumber] = addressLines[0].split(', ');
    const baseAddress = houseNumber ? `${street} ${houseNumber}` : street;

    let address = baseAddress;
    if (aptNumber) {
      address += ` ${aptNumber}`;
    }
    if (municipality) {
      address += `, ${municipality}`;
    }
    if (region) {
      address += `, ${region}`;
    }
    if (postalCode) {
      address += ` ${postalCode}`;
    }

    return address;
  },
);
export const selectAddressLine2 = createSelector(
  [selectServiceabilityDomain],
  (serviceabilityState: ServiceabilityState) => serviceabilityState.serviceAddress.addressLines.value[1],
);
export const selectInlineServiceAddress = createSelector(
  [selectServiceabilityDomain],
  ({ serviceAddress: { addressLines, municipality, region, postalCode } }) => {
    let address = `${addressLines.value[0]}${addressLines.value[1] && ''}`;
    municipality.value && (address += `, ${municipality.value}`);
    region.value && (address += `, ${region.value}`);
    postalCode.value && (address += ` ${postalCode.value}`);

    return address;
  },
);
export const selectInlineServiceStreetAndPostalCode = createSelector(
  [selectServiceabilityDomain, selectApp],
  ({ serviceAddress: { addressLines, municipality, region, postalCode, houseNumber } }) => {
    let address = `${addressLines.value[0]}`;

    houseNumber.value && (address += `${' ' + houseNumber.value}`);
    addressLines.value[1] &&
      (address += `${i18n.t('serviceability:common.address.appendApt') + addressLines.value[1]}`);
    municipality.value && (address += `, ${municipality.value}`);
    region.value && (address += `, ${region.value}`);
    postalCode.value && (address += ` ${postalCode.value} `);
    return address;
  },
);
export const selectIsVermontCustomer = createSelector(
  [selectServiceabilityDomain, selectAddressDomain],
  (
    {
      serviceAddress: {
        region: { value: serviceState },
      },
    },
    {
      billingAddress: {
        region: { value: billingState },
      },
      billingAddressEqualsServiceAddress,
    },
  ) => billingState === VERMONT || (serviceState === VERMONT && billingAddressEqualsServiceAddress),
);
export const selectServiceAddressIsValid = createSelector(
  [selectServiceabilityDomain],
  ({ serviceAddress }) =>
    !!(
      serviceAddress.addressLines.value?.[0].length &&
      serviceAddress.municipality.value.length &&
      serviceAddress.region.value.length &&
      serviceAddress.postalCode.value.length &&
      serviceAddress.countryCode.value.length
    ),
);

export const selectIsServiceAddressSet = createSelector(
  [selectServiceabilityDomain],
  (serviceabilityState: ServiceabilityState) => serviceabilityState.isServiceAddressSet,
);
